import { LitElement, html, css, nothing } from 'lit';
import { api, ApiError } from '../api.js';
import { StoreMixin } from '../store.js';
import { REMEDIES, REMEDY_LABELS, REMEDY_NOTES } from '../lib/decisions.js';

/**
 * How long each remedy takes (TRD 8.6; Design Review V-17; Phase 49).
 *
 * These four numbers are the only settings a planner ever has to argue about by name, because
 * they are the only ones that are a claim about **us** rather than about the forecast: how long
 * our hiring actually takes, how quickly we can get a known freelancer started. Every decision
 * deadline on the calendar is a window start minus one of them, so a number that is wrong here
 * makes every date on that page wrong in the same direction — and the people who know the true
 * figure are not the people who read the API.
 *
 * Writing them is Admin (`PATCH /api/v1/settings`), and the card says so to everybody else rather
 * than showing inputs that will be refused. Reading them needs no Admin: the decision calendar
 * carries the lead times it computed with, so this card reads them from there.
 */
class RemedyLeadTimes extends StoreMixin(LitElement) {
  static properties = {
    _leadTimes: { state: true },
    _byDiscipline: { state: true },
    _loading: { state: true },
    _saving: { state: true },
    _error: { state: true },
    _saved: { state: true },
  };

  constructor() {
    super();
    this._leadTimes = null;
    this._byDiscipline = {};
    this._loading = false;
    this._saving = false;
    this._error = null;
    this._saved = false;
  }

  static styles = css`
    :host {
      display: block;
    }
    .card {
      background: var(--surface);
      border: 1px solid var(--border-soft);
      border-radius: var(--radius-card);
      box-shadow: var(--shadow-card);
      padding: 20px 22px;
      max-width: 720px;
    }
    h2 {
      margin: 0 0 6px;
      font-size: 18px;
      color: var(--ink-strong);
    }
    .lede {
      margin: 0 0 16px;
      font-size: 13px;
      color: var(--ink-muted);
      max-width: 70ch;
    }
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: 10px;
    }
    li {
      display: flex;
      align-items: baseline;
      gap: 12px;
      font-size: 14px;
      color: var(--ink-strong);
    }
    .name {
      min-width: 16ch;
      font-weight: 600;
    }
    .name small {
      display: block;
      font-size: 12px;
      font-weight: 400;
      color: var(--ink-muted);
    }
    input[type='number'] {
      font: inherit;
      font-size: 13px;
      width: 5.5em;
      color: var(--ink);
      background: var(--control-bg);
      border: 1px solid var(--control-border);
      border-radius: var(--radius-pill);
      padding: 3px 9px;
      text-align: right;
    }
    .weeks {
      font-size: 12.5px;
      color: var(--ink-muted);
    }
    .value {
      font-variant-numeric: tabular-nums;
      font-weight: 600;
    }
    .override {
      font-size: 12px;
      color: var(--ink-muted);
      margin: 12px 0 0;
    }
    .note {
      font-size: 12px;
      color: var(--ink-muted);
      margin: 12px 0 0;
      max-width: 70ch;
    }
    .error {
      font-size: 13px;
      color: var(--status-overloaded);
      margin: 0 0 8px;
    }
    .saved {
      font-size: 12.5px;
      color: var(--status-healthy);
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    void this._load();
  }

  get _isAdmin() {
    const user = this.appState.user;
    return Boolean(user?.roles?.includes('admin') || user?.effective_role === 'admin');
  }

  /**
   * The lead times as they stand. An Admin reads them from settings; everybody else reads them
   * off the decision calendar, which computed its dates with them — one number, two doors, and
   * no Admin-only endpoint in a Viewer's way.
   */
  async _load() {
    this._loading = true;
    this._error = null;
    try {
      if (this._isAdmin) {
        const payload = await api.get('/settings');
        this._leadTimes = { ...payload.settings.remedies.lead_time_weeks };
        this._byDiscipline = payload.settings.remedies.by_discipline ?? {};
      } else {
        const payload = await api.get('/forecast/decisions', { limit: 1 });
        this._leadTimes = { ...payload.lead_times };
        this._byDiscipline = payload.by_discipline ?? {};
      }
    } catch (err) {
      this._error = err instanceof ApiError ? err.message : 'Could not read the lead times.';
    } finally {
      this._loading = false;
    }
  }

  /**
   * A number here is not the state, it is a request to change it — the digest control's rule, for
   * the same reason: a refused write must not leave a figure on screen that no deadline was
   * computed from.
   */
  async _set(remedy, event) {
    const wanted = Math.max(0, Math.round(Number(event.target.value)));
    const current = this._leadTimes?.[remedy] ?? 0;
    event.target.value = current;
    if (!Number.isFinite(wanted) || wanted === current) return;

    this._saving = true;
    this._saved = false;
    this._error = null;
    try {
      const payload = await api.patch('/settings', {
        remedies: { lead_time_weeks: { [remedy]: wanted } },
      });
      this._leadTimes = { ...payload.settings.remedies.lead_time_weeks };
      this._saved = true;
      // The calendar's dates are derived from these, so anything drawing them has to re-read.
      this.dispatchEvent(
        new CustomEvent('lead-times-changed', {
          detail: { lead_time_weeks: this._leadTimes },
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err) {
      this._error = err instanceof ApiError ? err.message : 'Could not save that.';
    } finally {
      this._saving = false;
    }
  }

  render() {
    const lead = this._leadTimes;
    const overrides = Object.entries(this._byDiscipline ?? {}).filter(
      ([, v]) => v && Object.keys(v).length,
    );
    return html`
      <div class="card">
        <h2>How long each remedy takes</h2>
        <p class="lede">
          Every decision deadline is an overload's first week minus one of these. They are a claim
          about us rather than about the forecast — how long our hiring actually takes — so a number
          that is wrong here makes every date on the decision calendar wrong in the same direction.
        </p>
        ${this._error ? html`<p class="error">${this._error}</p>` : nothing}
        ${
          !lead
            ? html`<p class="note">
                ${this._loading ? 'Reading the lead times…' : 'No lead times.'}
              </p>`
            : html`
                <ul>
                  ${REMEDIES.map(
                    (remedy) => html`
                      <li>
                        <span class="name">
                          ${REMEDY_LABELS[remedy]}
                          <small>${REMEDY_NOTES[remedy]}</small>
                        </span>
                        ${
                          this._isAdmin
                            ? html`<input
                                  type="number"
                                  min="0"
                                  max="104"
                                  step="1"
                                  data-remedy=${remedy}
                                  .value=${String(lead[remedy] ?? 0)}
                                  ?disabled=${this._saving}
                                  @change=${(e) => this._set(remedy, e)}
                                />
                                <span class="weeks">weeks</span>`
                            : html`<span class="value" data-remedy=${remedy}
                                  >${lead[remedy] ?? 0}</span
                                >
                                <span class="weeks">weeks</span>`
                        }
                      </li>
                    `,
                  )}
                </ul>
                ${
                  overrides.length
                    ? html`<p class="override" data-note="overrides">
                        ${overrides.length === 1 ? 'One role has' : `${overrides.length} roles have`}
                        their own lead times, which win over these:
                        ${overrides
                          .map(
                            ([service, own]) =>
                              `${service} (${Object.entries(own)
                                .map(([k, v]) => `${REMEDY_LABELS[k] ?? k} ${v}w`)
                                .join(', ')})`,
                          )
                          .join('; ')}.
                        Those are set through the API.
                      </p>`
                    : nothing
                }
                ${
                  this._isAdmin
                    ? html`<p class="note" data-note="effect">
                        Changing one of these moves every decision deadline computed from it at
                        once, without waiting for a new forecast run — the calendar says which dates
                        moved and what the run itself had said.
                        ${this._saved ? html`<span class="saved">Saved.</span>` : nothing}
                      </p>`
                    : html`<p class="note" data-note="admin">
                        These are Admin settings. The dates on the decision calendar are computed
                        from them, so this is what they are; ask an Admin to change one.
                      </p>`
                }
              `
        }
      </div>
    `;
  }
}

customElements.define('remedy-lead-times', RemedyLeadTimes);
